import EditableShape from '@recogito/annotorious/src/tools/EditableShape';
import { SVG_NAMESPACE } from '@recogito/annotorious/src/util/SVG';
import { setFormatterElSize } from '@recogito/annotorious/src/util/Formatting';
import { parseRectFragment } from '@recogito/annotorious/src/selectors/RectFragment';
import { getAnnotationStyle } from 'components/ImageViewer/annotorious/formatter';
import {
  createEllipseElement,
  getEllipseStyle,
  setEllipseSize,
  toEllipseTarget,
} from 'components/ImageViewer/annotorious/tools/ellipse/ellipseUtils';

const MIN_SIZE = 1;

/**
 * An editable ellipse shape.
 *
 * The annotation is stored as a FragmentSelector rectangle plus a
 * `shape: 'ellipse'` marker (see toEllipseTarget). While it is selected this
 * class draws the ellipse that the formatter would draw once it is deselected;
 * four handles sit on the ellipse itself (top/right/bottom/left) to resize it.
 */
export default class EditableEllipse extends EditableShape {
  constructor(annotation, g, config, env) {
    super(annotation, g, config, env);

    this.svg.addEventListener('mousemove', this.onMouseMove);
    this.svg.addEventListener('mouseup', this.onMouseUp);

    const { x, y, w, h } = parseRectFragment(annotation, env.image);
    const { color, strokeWidth } = getAnnotationStyle(annotation);
    const ellipseStyle = getEllipseStyle(color, strokeWidth);

    this.containerGroup = document.createElementNS(SVG_NAMESPACE, 'g');

    this.elementGroup = document.createElementNS(SVG_NAMESPACE, 'g');
    this.elementGroup.setAttribute('class', 'a9s-annotation editable selected');
    this.elementGroup.setAttribute('data-id', annotation.id);

    this.ellipseGroup = document.createElementNS(SVG_NAMESPACE, 'g');

    this.outerEllipse = createEllipseElement('a9s-outer');
    this.outerEllipse.setAttribute('style', ellipseStyle);
    this.innerEllipse = createEllipseElement('a9s-inner');
    this.innerEllipse.setAttribute('style', ellipseStyle);
    this.innerEllipse.addEventListener(
      'mousedown',
      this.onGrab(this.ellipseGroup),
    );

    this.ellipseGroup.appendChild(this.outerEllipse);
    this.ellipseGroup.appendChild(this.innerEllipse);
    this.elementGroup.appendChild(this.ellipseGroup);

    this.handles = [0, 1, 2, 3].map(() => {
      const handle = this.drawHandle(0, 0);
      handle.addEventListener('mousedown', this.onGrab(handle));
      this.elementGroup.appendChild(handle);

      return handle;
    });

    this.containerGroup.appendChild(this.elementGroup);
    g.appendChild(this.containerGroup);

    this.setSize(x, y, w, h);

    this.grabbedElem = null;
    this.mouseOffset = null;
  }

  onScaleChanged = () => this.handles.map(this.scaleHandle);

  setSize(x, y, w, h) {
    this.size = { x, y, w, h };

    setEllipseSize(this.outerEllipse, x, y, w, h);
    setEllipseSize(this.innerEllipse, x, y, w, h);
    setFormatterElSize(this.elementGroup, x, y, w, h);

    const [top, right, bottom, left] = this.handles;

    this.setHandleXY(top, x + w / 2, y);
    this.setHandleXY(right, x + w, y + h / 2);
    this.setHandleXY(bottom, x + w / 2, y + h);
    this.setHandleXY(left, x, y + h / 2);
  }

  /** Resize by dragging one of the four handles sitting on the ellipse. */
  stretchEdge = (draggedHandleIdx, mousePos) => {
    const { naturalWidth, naturalHeight } = this.env.image;
    const { x, y, w, h } = this.size;
    const clamp = (value, max) => Math.min(Math.max(value, 0), max);
    const next = { x, y, w, h };

    if (draggedHandleIdx === 0) {
      const bottom = y + h;

      next.y = clamp(mousePos.y, bottom - MIN_SIZE);
      next.h = Math.max(MIN_SIZE, bottom - next.y);
    } else if (draggedHandleIdx === 1) {
      next.w = Math.max(MIN_SIZE, clamp(mousePos.x, naturalWidth) - x);
    } else if (draggedHandleIdx === 2) {
      next.h = Math.max(MIN_SIZE, clamp(mousePos.y, naturalHeight) - y);
    } else {
      const right = x + w;

      next.x = clamp(mousePos.x, right - MIN_SIZE);
      next.w = Math.max(MIN_SIZE, right - next.x);
    }

    this.setSize(next.x, next.y, next.w, next.h);

    return next;
  };

  onGrab = (grabbedElem) => (evt) => {
    if (evt.button !== 0) return;

    this.grabbedElem = grabbedElem;

    const pos = this.getSVGPoint(evt);
    const { x, y } = this.size;

    this.mouseOffset = { x: pos.x - x, y: pos.y - y };
  };

  onMouseMove = (evt) => {
    if (evt.button !== 0) return;
    if (!this.grabbedElem) return;

    const constrain = (coord, max) => {
      if (coord < 0) return 0;
      if (coord > max) return max;

      return coord;
    };

    const pos = this.getSVGPoint(evt);

    if (this.grabbedElem === this.ellipseGroup) {
      const { w, h } = this.size;
      const { naturalWidth, naturalHeight } = this.env.image;

      const x = constrain(pos.x - this.mouseOffset.x, naturalWidth - w);
      const y = constrain(pos.y - this.mouseOffset.y, naturalHeight - h);

      this.setSize(x, y, w, h);
      this.emit('update', toEllipseTarget(x, y, w, h, this.env.image));
    } else {
      const handleIdx = this.handles.indexOf(this.grabbedElem);
      const { x, y, w, h } = this.stretchEdge(handleIdx, pos);

      this.emit('update', toEllipseTarget(x, y, w, h, this.env.image));
    }
  };

  onMouseUp = () => {
    this.grabbedElem = null;
    this.mouseOffset = null;
  };

  get element() {
    return this.elementGroup;
  }

  updateState = (annotation) => {
    const { x, y, w, h } = parseRectFragment(annotation, this.env.image);
    this.setSize(x, y, w, h);
  };

  destroy() {
    this.containerGroup.parentNode.removeChild(this.containerGroup);
    super.destroy();
  }
}
